/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import moment, {Moment} from 'moment';
import {TimeModificationAction} from '../daterangepicker/types';
import DigitalTimePicker from './DigitalTimePicker';

type CustomToolbarProps = {
  date?: Moment | null;
  onChange?: (value: Moment, isFinish?: boolean) => void;
};

const getClockDateValue = (date?: Moment | null): Date | undefined => (
  date
    ? new Date(
      date.year(),
      date.month(),
      date.date(),
      date.hour(),
      date.minute(),
      date.second(),
      date.millisecond()
    )
    : undefined
);

/**
 * A specialized toolbar component for Material UI Pickers that replaces the 
 * standard clock view with a DLS-compliant DigitalTimePicker. It allows for 
 * granular hour, minute, and meridiem adjustments via a digital interface.
 * * Note: This component is intended to be passed as a reference to the 
 * `ToolbarComponent` prop of a DateTimePicker.
 * 
 * * ```typescript
 * 
 * import {CustomToolbar} from '@ion/dls/src/components/date/CustomToolbar';
 * 
 * <DateTimePicker ToolbarComponent={CustomToolbar}
 *   {...otherProps} />
 * 
 * ```
 * 
 */
const CustomToolbar = ({
  date,
  onChange
}: CustomToolbarProps) => {
  const handleDigitalChange = useCallback((action: TimeModificationAction) => {
    if (date && onChange) {
      const next = moment(date);
      let hour   = moment(date).hour();
      let minute = moment(date).minute();

      switch (action) {
        case TimeModificationAction.HourUp:
          hour = (hour + 1) % 24;
          break;
        case TimeModificationAction.HourDown:
          hour = (hour - 23) % 24;
          break;
        case TimeModificationAction.MinuteUp:
          minute = (minute + 1) % 60;
          if (minute === 0) {
            hour = (hour + 1) % 24;
          }
          break;
        case TimeModificationAction.MinuteDown:
          minute = (minute - 59) % 60;
          if (minute === 59) {
            hour = (hour - 23) % 24;
          }
          break;
        case TimeModificationAction.ToggleMeridiem:
          hour = (hour + 12) % 24;
          break;
        default:
          break;
      }
      onChange(next.set({hour, minute}), false);
    }
  }, [date, onChange]);

  const handleSelectedTimeChange = useCallback((hour: number, minute: number) => {
    if (date && onChange) {
      onChange(moment(date).set({hour, minute}), false);
    }
  }, [date, onChange]);

  return (
    <div style={{padding: 16}}>
      <DigitalTimePicker dateValue={getClockDateValue(date)}
        onChangeTime={handleDigitalChange}
        onSelectTime={handleSelectedTimeChange} />
    </div>
  );
};

export default React.memo(CustomToolbar);
